/**
 * Key topics (parent FAQ topics) share the widget menu with the fixed buttons.
 * The chatbot sorts them all by position, smallest first, reading ``orderId``
 * before ``order_id``; fixed buttons default to 1-4, so topics come after them.
 */

export interface PositionedRecord {
  orderId?: unknown;
  order_id?: unknown;
}

/** A topic's position exactly as the chatbot reads it (0 when it has none). */
export function topicPosition(record: PositionedRecord | null | undefined): number {
  if (!record) return 0;
  const value = record.orderId !== undefined ? record.orderId : record.order_id;
  if (value === null || value === undefined || String(value).trim() === "") return 0;
  const order = Number(value);
  return Number.isInteger(order) ? order : 0;
}

/** Topics in widget order: smallest position first, ties keep their current order. */
export function sortByTopicPosition<T extends { order_id: number }>(topics: T[]): T[] {
  return topics
    .map((topic, index) => ({ topic, index }))
    .sort((a, b) => a.topic.order_id - b.topic.order_id || a.index - b.index)
    .map(({ topic }) => topic);
}

/** Position for a new topic: after every fixed button and every existing topic. */
export function nextTopicPosition(topics: { order_id: number }[], fixedMaxOrder: number): number {
  return Math.max(fixedMaxOrder, ...topics.map((topic) => Number(topic.order_id) || 0)) + 1;
}

/** Body for ``/key-topic-order``: each topic's place in the list (1 = first). */
export function topicOrderPayload(topics: { intentID: string }[]): { intentID: string; order: number }[] {
  return topics.map((topic, index) => ({ intentID: topic.intentID, order: index + 1 }));
}

/** True when the list is not in the given intent order. */
export function orderChanged(topics: { intentID: string }[], original: { intentID: string }[]): boolean {
  return topics.length !== original.length || topics.some((topic, index) => topic.intentID !== original[index]?.intentID);
}
